import { useState, useEffect, useRef } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  MoreHorizontal,
  AlertTriangle,
} from "lucide-react";
import { toast } from "react-toastify";
import { RichTextEditor } from "./RichTextEditor";
import {
  getArticles,
  createArticle,
  updateArticle,
  deleteArticle,
  getProductSections,
  flattenProductSections,
  getDocuments,
  createDocument,
  updateDocument,
  deleteDocument,
  setArticleStage,
  setArticleAI,
  setDocumentAI,
  ARTICLE_STAGES,
  getArticleVersions,
  getArticleVersion,
  restoreArticleVersion,
  getCategories,
  getArticleTags,
  ARTICLE_TAGS_MAX,
  getTranslationCompleteness,
  readApiError,
} from "./supportApi";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../../components/ui/dropdown-menu";
import { Switch } from "../../components/ui/switch";
import SupportConfirmationModal from "./SupportConfirmationModal";
import BulkArticleUpload from "./BulkArticleUpload";

const STAGE_LABELS = Object.fromEntries(
  ARTICLE_STAGES.map((s) => [s.status, s.label]),
);

/**
 * Whether an editor is empty as far as a reader is concerned.
 *
 * The editor hands back HTML, so `.trim()` cannot answer this: emptying a contentEditable
 * leaves a `<br>` behind, and deleting pasted text leaves the wrapper it came in
 * (`<div><pre …>` from Google Translate, for one). Both look blank on screen and both are
 * a non-empty string.
 *
 * Images and embeds count as content on their own — an article can be a diagram or a video
 * with nothing written around it.
 *
 * Exported for its test; a regex rather than the DOM so that test needs no browser.
 */
export const isBlankHtml = (html) =>
  !/<(img|iframe)\b/i.test(html) &&
  String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim() === "";

// Stage badge colours. Only `published` is publicly readable, so it is the only one that
// reads as "live".
const STAGE_STYLES = {
  draft: "border-border/70 bg-white/[0.04] text-muted-foreground",
  in_review: "border-amber-500/30 bg-amber-500/10 text-amber-400",
  published: "border-ginox-green/30 bg-ginox-green/10 text-ginox-green",
  internal_only: "border-blue-500/30 bg-blue-500/10 text-blue-400",
  deprecated: "border-red-500/30 bg-red-500/10 text-red-400",
};

const toArticleView = (apiArticle) => ({
  id: apiArticle.id || apiArticle._id,
  title: apiArticle.translations?.en?.title || apiArticle.title || "",
  content: apiArticle.translations?.en?.content || apiArticle.content || "",
  faTitle: apiArticle.translations?.fa?.title || "",
  faContent: apiArticle.translations?.fa?.content || "",
  category: apiArticle.productSection,
  // The article's own taxonomy. `category` above is the product section — a
  // different thing that happens to share the word.
  //
  // Note the asymmetry: these come back as { slug, name } objects but are *sent*
  // as bare slugs, so flatten them here and the form never has to think about it.
  articleCategory: apiArticle.category?.slug || "",
  articleCategoryName: apiArticle.category?.name || "",
  tags: (apiArticle.tags || []).map((t) => t.slug),
  tagNames: (apiArticle.tags || []).map((t) => t.name),
  status: apiArticle.status,
  useForAI: apiArticle.useForAI,
  // status === "published" && useForAI, already computed by the backend.
  inKnowledgeBase: apiArticle.inKnowledgeBase,
  createdAt: apiArticle.createdAt || apiArticle.created_at,
  updatedAt: apiArticle.updatedAt || apiArticle.updated_at,
});

function Pill({ className, children }) {
  return (
    <span
      className={`inline-block rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] ${className}`}
    >
      {children}
    </span>
  );
}

export default function ArticlesKBManagement() {
  const [articles, setArticles] = useState([]);
  const [activeAdminTab, setActiveAdminTab] = useState("articles");
  const [knowledgeBaseEntries, setKnowledgeBaseEntries] = useState([]);

  // Create refs for editors
  const enEditorRef = useRef(null);
  const faEditorRef = useRef(null);

  // Article states
  const [editingArticle, setEditingArticle] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [productSection, setProductSection] = useState("gidex");
  const [enTitle, setEnTitle] = useState("");
  const [enContent, setEnContent] = useState("");
  const [faTitle, setFaTitle] = useState("");
  const [faContent, setFaContent] = useState("");
  // Defaults on, matching the backend: every live article today feeds the bot, so opting
  // out is the rare case. Has no effect until the article is published.
  const [useForAI, setUseForAI] = useState(true);
  const [stagingArticleId, setStagingArticleId] = useState(null);
  const [togglingAIId, setTogglingAIId] = useState(null);

  // Version history — collapsed until asked for, so opening the editor does not spend a
  // request on history nobody looked at.
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState([]);
  const [isVersionsLoading, setIsVersionsLoading] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [deletingArticleId, setDeletingArticleId] = useState(null);
  const [isArticleSubmitting, setIsArticleSubmitting] = useState(false);

  // Filter states
  // "Category" here is the product section, kept as-is to avoid renaming the whole page.
  // The article category proper lives in `articleCategory` below.
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");
  const [availableCategories, setAvailableCategories] = useState([]);

  // Article taxonomy — one category, many tags. Both referenced by slug.
  const [taxonomyCategories, setTaxonomyCategories] = useState([]);
  const [taxonomyTags, setTaxonomyTags] = useState([]);
  const [articleCategory, setArticleCategory] = useState("");
  const [articleTags, setArticleTags] = useState([]);
  const [selectedTaxonomyFilter, setSelectedTaxonomyFilter] = useState("all");
  const [selectedTagFilter, setSelectedTagFilter] = useState("all");

  // Translation coverage. Fetched when its tab is first opened — the whole set arrives in
  // one response, so there is nothing to page through.
  const [translationRows, setTranslationRows] = useState([]);
  const [translationSummary, setTranslationSummary] = useState(null);
  const [isTranslationsLoading, setIsTranslationsLoading] = useState(false);

  // Knowledge Base pagination states
  const [kbPage, setKbPage] = useState(1);
  const [kbLimit] = useState(10);
  const [kbPagination, setKbPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [isKbLoading, setIsKbLoading] = useState(false);

  // Document form states
  const [showDocumentForm, setShowDocumentForm] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [isDocumentSubmitting, setIsDocumentSubmitting] = useState(false);
  const [deletingDocumentId, setDeletingDocumentId] = useState(null);

  // Article pagination states
  const [articlePage, setArticlePage] = useState(1);
  const [articleLimit] = useState(10);
  const [articlePagination, setArticlePagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });
  const [isArticlesLoading, setIsArticlesLoading] = useState(false);

  // Bulk upload state
  const [showBulkUpload, setShowBulkUpload] = useState(false);

  // Confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalType, setDeleteModalType] = useState(""); // "article" or "document"
  const [itemToDelete, setItemToDelete] = useState(null);

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-US");
  };

  // API Functions
  const fetchArticles = async (
    page = articlePage,
    productSection = selectedCategoryFilter,
    status = selectedStatusFilter,
    category = selectedTaxonomyFilter,
    tag = selectedTagFilter,
  ) => {
    setIsArticlesLoading(true);
    try {
      const data = await getArticles(
        page,
        articleLimit,
        productSection,
        status,
        category,
        tag,
      );
      setArticles((data.data || []).map(toArticleView));
      if (data.pagination) {
        setArticlePagination(data.pagination);
      }
    } catch (error) {
      console.error("Error fetching articles:", error);
    } finally {
      setIsArticlesLoading(false);
    }
  };

  const fetchKnowledgeBase = async (page = kbPage) => {
    setIsKbLoading(true);
    try {
      const data = await getDocuments(page, kbLimit);
      // `content` is deliberately dropped — the list ships the document's full text
      // (thousands of characters each) and nothing here renders it.
      const transformedEntries = (data.data || []).map((entry) => ({
        id: entry.id,
        title: entry.title,
        originalFilename: entry.originalFilename,
        useForAI: entry.useForAI,
        createdAt: entry.createdAt,
        updatedAt: entry.updatedAt,
      }));
      setKnowledgeBaseEntries(transformedEntries);
      if (data.pagination) {
        setKbPagination(data.pagination);
      }
    } catch (error) {
      console.error("Error fetching knowledge base:", error);
    } finally {
      setIsKbLoading(false);
    }
  };

  // Fetch articles when filters or page changes
  useEffect(() => {
    fetchArticles(
      articlePage,
      selectedCategoryFilter,
      selectedStatusFilter,
      selectedTaxonomyFilter,
      selectedTagFilter,
    );
  }, [
    articlePage,
    selectedCategoryFilter,
    selectedStatusFilter,
    selectedTaxonomyFilter,
    selectedTagFilter,
  ]);

  // Fetch knowledge base when page changes
  useEffect(() => {
    fetchKnowledgeBase(kbPage);
  }, [kbPage]);

  // Re-read on every visit, so a translation written on the articles tab shows as done
  // when the user comes back here.
  useEffect(() => {
    if (activeAdminTab !== "translations") return;
    const fetchCompleteness = async () => {
      setIsTranslationsLoading(true);
      try {
        const data = await getTranslationCompleteness();
        setTranslationRows(data.articles || []);
        setTranslationSummary(data.summary || null);
      } catch (error) {
        console.error("Error fetching translation completeness:", error);
        toast.error("Failed to load translation coverage");
      } finally {
        setIsTranslationsLoading(false);
      }
    };
    fetchCompleteness();
  }, [activeAdminTab]);

  // Fetch product sections on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        // Flattened: the endpoint nests sub-products under their parent, so mapping the
        // top level alone drops them and an article can never be filed against one.
        setAvailableCategories(flattenProductSections(await getProductSections()));
      } catch (e) {
        console.error("Error fetching product sections", e);
      }
    };
    fetchCategories();
  }, []);

  // Article taxonomy, for the form pickers and the two filters.
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const [categories, tags] = await Promise.all([
          getCategories(),
          getArticleTags(),
        ]);
        setTaxonomyCategories(categories.categories || []);
        setTaxonomyTags(tags.tags || []);
      } catch (e) {
        console.error("Error fetching article taxonomy", e);
      }
    };
    fetchTaxonomy();
  }, []);

  const handleDeleteArticle = async (id) => {
    setDeletingArticleId(id);
    try {
      const response = await deleteArticle(id);
      if (response.ok || response.status === 204) {
        toast.success("Article deleted successfully");
        refreshArticles();
      } else {
        toast.error(await readApiError(response, "Could not delete the article"));
      }
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Could not delete the article. Please try again.");
    } finally {
      setDeletingArticleId(null);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Get latest content directly from editor refs
    const latestEnContent = enEditorRef.current?.getHTML() || enContent;
    const latestFaContent = faEditorRef.current?.getHTML() || faContent;

    if (!enTitle.trim() || isBlankHtml(latestEnContent)) {
      toast.error("The English title and content are both required");
      return;
    }

    // A translation is a title and a body together — the backend refuses half of one, and
    // half of one is not something anyone means to save. Either fill both Persian fields or
    // leave them empty; empty is how a Persian translation is removed.
    const faTitleFilled = !!faTitle.trim();
    const faContentFilled = !isBlankHtml(latestFaContent);
    if (faTitleFilled !== faContentFilled) {
      toast.error(
        faTitleFilled
          ? "The Persian translation needs content as well as a title"
          : "The Persian translation needs a title as well as content",
      );
      return;
    }

    setIsArticleSubmitting(true);

    const articleData = {
      productSection,
      en: {
        title: enTitle,
        content: latestEnContent, // ✅ Use latest content from ref
      },
    };

    // Only on create. The edit endpoint takes content and product section only — the AI
    // switch has its own endpoint so the audit log records what actually happened.
    if (!editingArticle) {
      articleData.useForAI = useForAI;
    }

    // Taxonomy goes on both create and update, unlike useForAI. `null` clears the
    // category; `tags` is the complete set the article should end up with, not an
    // addition, so the whole list has to go every time.
    articleData.category = articleCategory || null;
    articleData.tags = articleTags;

    // Both fields or neither — the check above has already established that.
    if (faTitleFilled) {
      articleData.fa = {
        title: faTitle,
        content: latestFaContent, // ✅ Use latest content from ref
      };
    } else if (editingArticle) {
      // Empty on an edit means "remove the Persian translation". On create there is
      // nothing to remove, and `null` is not something the create endpoint accepts.
      articleData.fa = null;
    }

    try {
      let response;
      if (editingArticle) {
        response = await updateArticle(editingArticle.id, articleData);
      } else {
        response = await createArticle(articleData);
      }

      if (response.ok || response.status === 200 || response.status === 201) {
        toast.success(
          editingArticle
            ? "Article updated successfully"
            : "Article created successfully",
        );
        setProductSection("gidex");
        setEnTitle("");
        setEnContent("");
        setFaTitle("");
        setFaContent("");
        setUseForAI(true);
        setArticleCategory("");
        setArticleTags([]);
        setEditingArticle(null);
        resetVersionPanel();
        setShowForm(false);
        await refreshArticles();
      } else {
        toast.error(
          await readApiError(
            response,
            `Could not ${editingArticle ? "update" : "create"} the article`,
          ),
        );
      }
    } catch (error) {
      console.error(
        `Error ${editingArticle ? "updating" : "creating"} article:`,
        error,
      );
      toast.error(
        `Failed to ${
          editingArticle ? "update" : "create"
        } article. Please try again.`,
      );
    } finally {
      setIsArticleSubmitting(false);
    }
  };

  const openArticleEditor = (article) => {
    setEditingArticle(article);
    resetVersionPanel();
    setShowForm(true);
    setShowBulkUpload(false);
    setProductSection(article.category || "gidex");
    setEnTitle(article.title || "");
    setEnContent(article.content || "");
    setFaTitle(article.faTitle || "");
    setFaContent(article.faContent || "");
    // useForAI is not touched — the form only offers it on create, and it has its own
    // endpoint from the row menu.
    // Must be prefilled: `tags` replaces the whole set on save, so an empty picker
    // would strip them.
    setArticleCategory(article.articleCategory || "");
    setArticleTags(article.tags || []);
  };

  // The completeness list is a summary — no bodies — so opening one for editing means
  // going and getting the article itself. 44 articles fit in one page.
  const openArticleFromTranslations = async (row) => {
    try {
      const data = await getArticles(1, 100);
      const match = (data.data || []).find((a) => (a.id || a._id) === row.id);
      if (!match) {
        toast.error("That article could not be loaded");
        return;
      }
      setActiveAdminTab("articles");
      openArticleEditor(toArticleView(match));
    } catch (error) {
      console.error("Error opening article:", error);
      toast.error("That article could not be loaded");
    }
  };

  const refreshArticles = () =>
    fetchArticles(
      articlePage,
      selectedCategoryFilter,
      selectedStatusFilter,
      selectedTaxonomyFilter,
      selectedTagFilter,
    );

  const handleStageChange = async (article, stage) => {
    setStagingArticleId(article.id);
    try {
      const response = await setArticleStage(article.id, stage.action);
      if (response.ok) {
        toast.success(`Moved to ${stage.label}`);
        await refreshArticles();
        return;
      }
      toast.error(await readApiError(response, "Could not change the stage"));
    } catch (error) {
      console.error("Error changing article stage:", error);
      toast.error("Could not change the stage. Please try again.");
    } finally {
      setStagingArticleId(null);
    }
  };

  const handleArticleAIToggle = async (article, next) => {
    setTogglingAIId(article.id);
    try {
      const response = await setArticleAI(article.id, next);
      if (response.ok) {
        toast.success(next ? "AI enabled" : "AI disabled");
        await refreshArticles();
        return;
      }
      toast.error(await readApiError(response, "Could not change the AI switch"));
    } catch (error) {
      console.error("Error toggling article AI:", error);
      toast.error("Could not change the AI switch. Please try again.");
    } finally {
      setTogglingAIId(null);
    }
  };

  const handleDocumentAIToggle = async (entry, next) => {
    setTogglingAIId(entry.id);
    try {
      const response = await setDocumentAI(entry.id, next);
      if (response.ok) {
        toast.success(next ? "AI enabled" : "AI disabled");
        await fetchKnowledgeBase(kbPage);
        return;
      }
      // 409 means the document predates text storage, so there is nothing to re-index.
      // The backend's message already says to upload the file again — show it verbatim.
      toast.error(await readApiError(response, "Could not change the AI switch"));
    } catch (error) {
      console.error("Error toggling document AI:", error);
      toast.error("Could not change the AI switch. Please try again.");
    } finally {
      setTogglingAIId(null);
    }
  };

  /** Wipe the panel so a different article never shows the previous one's history. */
  const resetVersionPanel = () => {
    setShowVersions(false);
    setVersions([]);
    setPreviewVersion(null);
  };

  const fetchVersions = async (articleId) => {
    setIsVersionsLoading(true);
    try {
      const data = await getArticleVersions(articleId, 1, 50);
      setVersions(data.data || []);
    } catch (error) {
      console.error("Error fetching article versions:", error);
      toast.error("Could not load the version history");
    } finally {
      setIsVersionsLoading(false);
    }
  };

  const toggleVersions = () => {
    const next = !showVersions;
    setShowVersions(next);
    if (next && editingArticle) fetchVersions(editingArticle.id);
  };

  const handlePreviewVersion = async (versionNumber) => {
    setIsPreviewLoading(true);
    try {
      const version = await getArticleVersion(editingArticle.id, versionNumber);
      setPreviewVersion(version);
    } catch (error) {
      console.error("Error loading version:", error);
      toast.error("Could not load that version");
    } finally {
      setIsPreviewLoading(false);
    }
  };

  /** Languages the article has right now, so a restore that drops one can be flagged. */
  const currentLanguages = () => {
    const langs = ["en"];
    if (faTitle.trim() || !isBlankHtml(faContent)) langs.push("fa");
    return langs;
  };

  const handleRestoreVersion = async (versionNumber) => {
    setIsRestoring(true);
    try {
      const response = await restoreArticleVersion(editingArticle.id, versionNumber);
      if (response.ok) {
        // The response is the updated article, so refill the form straight from it rather
        // than re-fetching.
        const article = await response.json();
        const en = article.translations?.en;
        const fa = article.translations?.fa;
        setProductSection(article.productSection || "gidex");
        setEnTitle(en?.title || "");
        setEnContent(en?.content || "");
        setFaTitle(fa?.title || "");
        // RichTextEditor mirrors its `value` prop, so setting state is enough to repaint
        // both editors — there is no imperative setter.
        setFaContent(fa?.content || "");

        toast.success(`Restored version ${versionNumber}`);
        setPreviewVersion(null);
        await fetchVersions(editingArticle.id);
        await refreshArticles();
        return;
      }
      // 409 has two different causes — a deleted product or a concurrent edit — and the
      // message says which, so show it as-is.
      toast.error(await readApiError(response, "Could not restore that version"));
    } catch (error) {
      console.error("Error restoring version:", error);
      toast.error("Could not restore that version. Please try again.");
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDeleteArticleClick = (article) => {
    setItemToDelete(article);
    setDeleteModalType("article");
    setShowDeleteModal(true);
  };

  const handleDeleteDocumentClick = (doc) => {
    setItemToDelete(doc);
    setDeleteModalType("document");
    setShowDeleteModal(true);
  };

  const handleDeleteDocument = async (id) => {
    setDeletingDocumentId(id);
    try {
      const response = await deleteDocument(id);
      if (response.ok || response.status === 204) {
        toast.success("Document deleted successfully");
        await fetchKnowledgeBase(kbPage);
      } else {
        toast.error(await readApiError(response, "Could not delete the document"));
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      toast.error("Could not delete the document. Please try again.");
    } finally {
      setDeletingDocumentId(null);
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
  };

  const handleConfirmDelete = () => {
    if (deleteModalType === "article" && itemToDelete) {
      handleDeleteArticle(itemToDelete.id);
    } else if (deleteModalType === "document" && itemToDelete) {
      handleDeleteDocument(itemToDelete.id);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setItemToDelete(null);
    setDeleteModalType("");
  };

  const handleDocumentSubmit = async (e) => {
    e.preventDefault();
    if (!docTitle.trim()) {
      toast.error("Please enter a title");
      return;
    }
    if (!editingDocument && !docFile) {
      toast.error("Please select a file");
      return;
    }
    setIsDocumentSubmitting(true);
    try {
      let response;
      if (editingDocument) {
        response = await updateDocument(editingDocument.id, docTitle, docFile);
      } else {
        response = await createDocument(docTitle, docFile);
      }
      if (response.ok || response.status === 200 || response.status === 201) {
        toast.success(
          editingDocument
            ? "Document updated successfully"
            : "Document uploaded successfully",
        );
        setDocTitle("");
        setDocFile(null);
        setEditingDocument(null);
        setShowDocumentForm(false);
        await fetchKnowledgeBase(kbPage);
      } else {
        toast.error(
          await readApiError(
            response,
            `Could not ${editingDocument ? "update" : "upload"} the document`,
          ),
        );
      }
    } catch (error) {
      console.error(
        `Error ${editingDocument ? "updating" : "uploading"} document:`,
        error,
      );
      toast.error(
        `Failed to ${
          editingDocument ? "update" : "upload"
        } document. Please try again.`,
      );
    } finally {
      setIsDocumentSubmitting(false);
    }
  };

  // No need for client-side filtering anymore since API handles it
  const filteredArticles = articles;
  // No need for client-side filtering anymore since API handles it
  const filteredKnowledgeBaseEntries = knowledgeBaseEntries;

  const renderKnowledgeBaseTable = (entries) => (
    <div className="flex flex-col w-full gap-10">
      <div className="mb-8">
        {isKbLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : entries.length === 0 ? (
          <p className="text-muted-foreground">No documents found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
              <thead>
                <tr>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Title
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Original Filename
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    AI
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Created
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Updated
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr
                    key={entry.id}
                    className="border-b border-border/40 hover:bg-white/[0.04] transition"
                  >
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                      {entry.title}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground">
                      {entry.originalFilename || "-"}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex justify-center">
                        <Switch
                          checked={!!entry.useForAI}
                          disabled={togglingAIId === entry.id}
                          onCheckedChange={(next) =>
                            handleDocumentAIToggle(entry, next)
                          }
                          aria-label="Let the AI use this document"
                        />
                      </div>
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(entry.createdAt)}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(entry.updatedAt)}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex flex-col justify-center items-center gap-2 text-center">
                        <button
                          onClick={() => {
                            setEditingDocument(entry);
                            setDocTitle(entry.title);
                            setDocFile(null);
                            setShowDocumentForm(true);
                          }}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDocumentClick(entry)}
                          disabled={deletingDocumentId === entry.id}
                          className={`rounded-md px-3 py-1.5 text-xs transition-colors ${
                            deletingDocumentId === entry.id
                              ? "border border-border/40 bg-white/[0.03] text-muted-foreground cursor-not-allowed"
                              : "border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20"
                          }`}
                        >
                          {deletingDocumentId === entry.id
                            ? "Deleting..."
                            : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {kbPagination.totalPages > 1 && (
          <div className="flex items-center justify-between py-3 text-xs font-mono text-muted-foreground border-t border-border/40 mt-4">
            <span className="tabular-nums">
              {entries.length === 0 ? "0 documents" : `${(kbPage - 1) * kbLimit + 1} - ${Math.min(kbPage * kbLimit, kbPagination.total)} of ${kbPagination.total} documents`}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setKbPage((prev) => Math.max(1, prev - 1))}
                disabled={kbPage === 1}
                className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="tabular-nums select-none">{kbPage} / {kbPagination.totalPages || 1}</span>
              <button
                onClick={() => setKbPage((prev) => Math.min(kbPagination.totalPages, prev + 1))}
                disabled={kbPage >= kbPagination.totalPages}
                className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderAdminArticleTable = (items, title) => (
    <div className="flex flex-col w-full gap-10">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-foreground">
          {title}
        </h2>
        <div className="md:flex md:flex-row flex flex-col gap-4 mb-4">
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Category
            </label>
            <select
              value={selectedCategoryFilter}
              onChange={(e) => {
                setSelectedCategoryFilter(e.target.value);
                setArticlePage(1); // Reset to first page on filter change
              }}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 min-w-[150px]"
            >
              <option value="all">All Categories</option>
              {availableCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Status
            </label>
            <select
              value={selectedStatusFilter}
              onChange={(e) => {
                setSelectedStatusFilter(e.target.value);
                setArticlePage(1); // Reset to first page on filter change
              }}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 min-w-[150px]"
            >
              <option value="all">All Statuses</option>
              {ARTICLE_STAGES.map((stage) => (
                <option key={stage.status} value={stage.status}>
                  {stage.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Article Category
            </label>
            <select
              value={selectedTaxonomyFilter}
              onChange={(e) => {
                setSelectedTaxonomyFilter(e.target.value);
                setArticlePage(1);
              }}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 min-w-[150px]"
            >
              <option value="all">All Categories</option>
              {taxonomyCategories.map((cat) => (
                <option key={cat.id} value={cat.slug}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Tag
            </label>
            <select
              value={selectedTagFilter}
              onChange={(e) => {
                setSelectedTagFilter(e.target.value);
                setArticlePage(1);
              }}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 min-w-[150px]"
            >
              <option value="all">All Tags</option>
              {taxonomyTags.map((t) => (
                <option key={t.id} value={t.slug}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          {/* <div className="flex flex-col">
            <label
              className="text-sm font-semibold mb-2 text-white"
              style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
            >
              Tags
            </label>
            <select
              value={selectedTagFilter}
              onChange={(e) => {
                setSelectedTagFilter(e.target.value);
                setArticlePage(1); // Reset to first page on filter change
              }}
              className="px-4 py-2 text-white border bg-[#232323] border-[#146b4f] rounded-lg min-w-[150px]"
              style={{ fontFamily: '"Scarlex Regular", sans-serif' }}
            >
              <option value="all">All Tags</option>
              {availableTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </div> */}
        </div>
        {isArticlesLoading ? (
          <p className="text-muted-foreground">Loading...</p>
        ) : items.length === 0 ? (
          <p className="text-muted-foreground">No articles found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
              <thead>
                <tr>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Title
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Status
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    AI
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Updated
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((article) => (
                  <tr
                    key={article.id}
                    className="hover:bg-white/[0.04] transition"
                  >
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                      {article.title}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        <Pill
                          className={
                            STAGE_STYLES[article.status] || STAGE_STYLES.draft
                          }
                        >
                          {STAGE_LABELS[article.status] || article.status}
                        </Pill>
                        {article.inKnowledgeBase && (
                          <Pill className="border-ginox-green/30 bg-ginox-green/10 text-ginox-green">
                            Trusted
                          </Pill>
                        )}
                        {article.useForAI === false && (
                          <Pill className="border-border/70 bg-white/[0.04] text-muted-foreground">
                            AI-Disabled
                          </Pill>
                        )}
                        {article.articleCategoryName && (
                          <Pill className="border-blue-500/30 bg-blue-500/10 text-blue-400 normal-case tracking-normal">
                            {article.articleCategoryName}
                          </Pill>
                        )}
                        {(article.tagNames || []).map((name) => (
                          <Pill
                            key={name}
                            className="border-border/70 bg-white/[0.04] text-muted-foreground normal-case tracking-normal"
                          >
                            {name}
                          </Pill>
                        ))}
                      </div>
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex justify-center">
                        <Switch
                          checked={!!article.useForAI}
                          disabled={togglingAIId === article.id}
                          onCheckedChange={(next) =>
                            handleArticleAIToggle(article, next)
                          }
                          aria-label="Let the AI use this article"
                        />
                      </div>
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground">
                      {formatDate(article.updatedAt)}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex justify-center">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              disabled={
                                stagingArticleId === article.id ||
                                deletingArticleId === article.id
                              }
                              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
                              aria-label="Article actions"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="w-52">
                            <DropdownMenuItem
                              onSelect={() => openArticleEditor(article)}
                            >
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {/* Any stage can follow any other, so only the one the
                                article is already in is disabled. */}
                            {ARTICLE_STAGES.map((stage) => (
                              <DropdownMenuItem
                                key={stage.action}
                                disabled={article.status === stage.status}
                                onSelect={() => handleStageChange(article, stage)}
                              >
                                {stage.menuLabel}
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-400 focus:text-red-400"
                              onSelect={() => handleDeleteArticleClick(article)}
                            >
                              {deletingArticleId === article.id
                                ? "Deleting..."
                                : "Delete"}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination Controls */}
        {articlePagination.totalPages > 1 && (
          <div className="flex items-center justify-between py-3 text-xs font-mono text-muted-foreground border-t border-border/40 mt-4">
            <span className="tabular-nums">
              {items.length === 0 ? "0 articles" : `${(articlePage - 1) * articleLimit + 1} - ${Math.min(articlePage * articleLimit, articlePagination.total)} of ${articlePagination.total} articles`}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setArticlePage((prev) => Math.max(1, prev - 1))}
                disabled={articlePage === 1}
                className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <span className="tabular-nums select-none">{articlePage} / {articlePagination.totalPages || 1}</span>
              <button
                onClick={() => setArticlePage((prev) => Math.min(articlePagination.totalPages, prev + 1))}
                disabled={articlePage >= articlePagination.totalPages}
                className="flex h-6 w-6 items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderArticleForm = () => (
    <div className="max-w-4xl rounded-lg border border-border/70 bg-card p-6 space-y-4">
      <h2 className="text-2xl font-bold text-foreground">
        {editingArticle ? "Edit Article" : "Create New Article"}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
            Product Section
          </label>
          <select
            value={productSection}
            onChange={(e) => setProductSection(e.target.value)}
            className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
          >
            {availableCategories.map((cat) => (
              <option key={cat.id} value={cat.id}>
                {cat.label}
              </option>
            ))}
          </select>
        </div>

        {/* Article taxonomy — one category, up to ARTICLE_TAGS_MAX tags */}
        <div>
          <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
            Category
          </label>
          <select
            value={articleCategory}
            onChange={(e) => setArticleCategory(e.target.value)}
            className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
          >
            <option value="">No category</option>
            {taxonomyCategories.map((cat) => (
              <option key={cat.id} value={cat.slug}>
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground">
              Tags
            </label>
            <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
              {articleTags.length} / {ARTICLE_TAGS_MAX}
            </span>
          </div>
          {taxonomyTags.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No tags yet — add some under Article Categories &amp; Tags.
            </p>
          ) : (
            <div className="max-h-44 space-y-1.5 overflow-y-auto rounded-md border border-border/70 bg-white/[0.02] p-3">
              {taxonomyTags.map((t) => {
                const checked = articleTags.includes(t.slug);
                // At the cap, only the already-chosen boxes stay usable, so a tag can
                // still be swapped out without clearing the lot first.
                const atCap = !checked && articleTags.length >= ARTICLE_TAGS_MAX;
                return (
                  <label
                    key={t.id}
                    className={`flex items-center gap-2 text-xs ${
                      atCap
                        ? "cursor-not-allowed text-muted-foreground/40"
                        : "cursor-pointer text-foreground"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={atCap}
                      onChange={(e) =>
                        setArticleTags((prev) =>
                          e.target.checked
                            ? [...prev, t.slug]
                            : prev.filter((s) => s !== t.slug),
                        )
                      }
                      className="h-3.5 w-3.5 rounded border-border/70 bg-white/[0.03] text-ginox-green focus:ring-ginox-green/30"
                    />
                    {t.name}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* English Section */}
        <div className="rounded-lg border border-border/70 bg-card p-6 space-y-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            English (Required)
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Title
              </label>
              <input
                type="text"
                value={enTitle}
                onChange={(e) => setEnTitle(e.target.value)}
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                placeholder="Enter English title..."
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Content
              </label>
              <RichTextEditor
                ref={enEditorRef}
                value={enContent}
                onChange={setEnContent}
                placeholder="Enter English content..."
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Persian Section */}
        <div className="rounded-lg border border-border/70 bg-card p-6 space-y-4">
          <h3 className="text-lg font-semibold text-foreground mb-4">
            Persian (Optional)
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Title
              </label>
              <input
                type="text"
                value={faTitle}
                onChange={(e) => setFaTitle(e.target.value)}
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                placeholder="Enter Persian title..."
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Content
              </label>
              <RichTextEditor
                ref={faEditorRef}
                value={faContent}
                onChange={setFaContent}
                placeholder="Enter Persian content..."
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* AI switch + draft notice - Only for Create */}
        {!editingArticle && (
          <div className="space-y-3 p-4 rounded-lg border border-border/70 bg-card">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="useForAI"
                checked={useForAI}
                onChange={(e) => setUseForAI(e.target.checked)}
                className="w-4 h-4 text-ginox-green bg-white/[0.03] border-border/70 rounded focus:ring-ginox-green/30"
              />
              <label
                htmlFor="useForAI"
                className="text-sm font-semibold text-foreground cursor-pointer"
              >
                Let the AI use this article
              </label>
            </div>
            <p className="text-xs leading-relaxed text-muted-foreground">
              New articles are saved as a <strong>draft</strong> and are not visible to
              customers. Publish it from the article list when it is ready. The AI only
              uses published articles with this switch on.
            </p>
          </div>
        )}

        <div className="md:flex md:flex-row md:gap-4 flex flex-col gap-3">
          <button
            type="submit"
            disabled={isArticleSubmitting}
            className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
              isArticleSubmitting
                ? "border-border/40 bg-white/[0.03] text-muted-foreground cursor-not-allowed"
                : "border-ginox-green/60 bg-ginox-green/10 text-ginox-green hover:bg-ginox-green/20"
            }`}
          >
            {isArticleSubmitting
              ? "Processing..."
              : editingArticle
                ? "Update Article"
                : "Create Article"}
          </button>
          <button
            type="button"
            onClick={() => {
              setShowForm(false);
              setEditingArticle(null);
              resetVersionPanel();
              setProductSection("gidex");
              setEnTitle("");
              setEnContent("");
              setFaTitle("");
              setFaContent("");
              setUseForAI(true);
              setArticleCategory("");
              setArticleTags([]);
            }}
            className="flex-1 rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>

      {/* Version history — only an existing article has one. */}
      {editingArticle && (
        <div className="border-t border-border/70 pt-4">
          <button
            type="button"
            onClick={toggleVersions}
            className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground hover:text-foreground transition-colors"
          >
            {showVersions ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
            Version history
            {versions.length > 0 && ` (${versions.length})`}
          </button>

          {showVersions && (
            <div className="mt-3">
              {isVersionsLoading ? (
                <div className="py-4 flex justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-ginox-green" />
                </div>
              ) : versions.length === 0 ? (
                <p className="text-xs text-muted-foreground">No versions recorded.</p>
              ) : (
                <div className="space-y-1.5">
                  {versions.map((v, index) => (
                    <div
                      key={v.versionNumber}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border/70 bg-white/[0.02] px-3 py-2 text-xs"
                    >
                      <span className="font-mono text-foreground">
                        v{v.versionNumber}
                      </span>
                      {/* Articles that predate this feature have a backfilled v1 whose
                          editor is genuinely unknown — never render a blank cell. */}
                      <span className="text-muted-foreground">
                        {v.editorName || "Unknown"}
                      </span>
                      <span className="font-mono uppercase text-muted-foreground/70">
                        {(v.languages || []).join(", ") || "—"}
                      </span>
                      <span className="text-muted-foreground/70">
                        {(v.changedFields || []).join(", ") || "—"}
                      </span>
                      <span className="text-muted-foreground/70">
                        {formatDate(v.createdAt)}
                      </span>
                      <div className="ml-auto flex items-center gap-2">
                        {/* The newest version is what the article already is, so restoring
                            it does nothing at all. */}
                        {index === 0 ? (
                          <span className="rounded-md border border-ginox-green/30 bg-ginox-green/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.08em] text-ginox-green">
                            Current
                          </span>
                        ) : (
                          <button
                            type="button"
                            disabled={isPreviewLoading}
                            onClick={() => handlePreviewVersion(v.versionNumber)}
                            className="rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
                          >
                            Preview
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen">
      <div className="container mx-auto px-4 py-8">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Support
          </div>
          <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">Articles & <span className="text-gradient-ginox">Knowledge Base</span></h1>
          <p className="text-sm text-muted-foreground mb-4">Manage articles and knowledge base documents.</p>
        </div>

        <div className="md:flex md:flex-row flex flex-col gap-3 mb-8">
          <button
            onClick={() => setActiveAdminTab("articles")}
            className={
              activeAdminTab === "articles"
                ? "rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green"
                : "rounded-md border border-border/70 bg-white/[0.03] px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            }
          >
            Article Management
          </button>
          <button
            onClick={() => setActiveAdminTab("knowledge-base")}
            className={
              activeAdminTab === "knowledge-base"
                ? "rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green"
                : "rounded-md border border-border/70 bg-white/[0.03] px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            }
          >
            Knowledge Base Management
          </button>
          <button
            onClick={() => setActiveAdminTab("translations")}
            className={
              activeAdminTab === "translations"
                ? "rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green"
                : "rounded-md border border-border/70 bg-white/[0.03] px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
            }
          >
            Translations
          </button>
        </div>

        {activeAdminTab === "articles" && (
          <div className="space-y-8">
            <div className="flex md:flex-row flex-col gap-6 md:gap-0 justify-between md:items-center">
              <h2 className="text-2xl font-bold text-foreground">
                Article Management
              </h2>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowBulkUpload(true);
                    setShowForm(false);
                  }}
                  className="rounded-md border border-white/20 bg-white/10 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/20 transition-colors w-fit"
                >
                  Bulk Upload (.docx)
                </button>
                <button
                  onClick={() => {
                    setShowForm(true);
                    setShowBulkUpload(false);
                    setEditingArticle(null);
                    resetVersionPanel();
                    setProductSection("gidex");
                    setEnTitle("");
                    setEnContent("");
                    setFaTitle("");
                    setFaContent("");
                    setUseForAI(true);
                    setArticleCategory("");
                    setArticleTags([]);
                  }}
                  className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors w-fit"
                >
                  Create New Article
                </button>
              </div>
            </div>
            {showBulkUpload && (
              <BulkArticleUpload
                availableCategories={availableCategories}
                onClose={() => setShowBulkUpload(false)}
                onComplete={() => {
                  setShowBulkUpload(false);
                  refreshArticles();
                }}
              />
            )}
            {showForm && !showBulkUpload && renderArticleForm()}
            {!showForm && !showBulkUpload && (
              <div className="space-y-8">
                {renderAdminArticleTable(filteredArticles, "All Articles")}
              </div>
            )}
          </div>
        )}

        {activeAdminTab === "knowledge-base" && (
          <div className="space-y-8">
            <div className="flex md:flex-row flex-col gap-6 md:gap-0 justify-between md:items-center">
              <h2 className="text-2xl font-bold text-foreground">
                Documents Management
              </h2>
              <button
                onClick={() => {
                  setShowDocumentForm(true);
                  setEditingDocument(null);
                  setDocTitle("");
                  setDocFile(null);
                }}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors w-fit"
              >
                Upload New Document
              </button>
            </div>
            {showDocumentForm && (
              <div className="max-w-2xl rounded-lg border border-border/70 bg-card p-6 space-y-4">
                <h2 className="text-2xl font-bold text-foreground">
                  {editingDocument ? "Edit Document" : "Upload New Document"}
                </h2>
                <form onSubmit={handleDocumentSubmit} className="space-y-6">
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                      Title
                    </label>
                    <input
                      type="text"
                      value={docTitle}
                      onChange={(e) => setDocTitle(e.target.value)}
                      className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                      placeholder="Enter document title..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                      {editingDocument
                        ? "Replace File (Optional - PDF, DOCX, TXT, MD - Max 20MB)"
                        : "File (PDF, DOCX, TXT, MD - Max 20MB)"}
                    </label>
                    <input
                      type="file"
                      accept=".pdf,.docx,.txt,.md"
                      onChange={(e) =>
                        setDocFile(e.target.files ? e.target.files[0] : null)
                      }
                      className="w-full rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 file:bg-transparent file:border-none file:text-muted-foreground"
                      required={!editingDocument}
                    />
                    {editingDocument && !docFile && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Current file: {editingDocument.originalFilename}
                      </p>
                    )}
                    {docFile && (
                      <p className="mt-2 text-sm text-muted-foreground">
                        Selected: {docFile.name}
                      </p>
                    )}
                  </div>
                  <div className="md:flex md:flex-row md:gap-4 flex flex-col gap-3">
                    <button
                      type="submit"
                      disabled={isDocumentSubmitting}
                      className={`flex-1 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                        isDocumentSubmitting
                          ? "border-border/40 bg-white/[0.03] text-muted-foreground cursor-not-allowed"
                          : "border-ginox-green/60 bg-ginox-green/10 text-ginox-green hover:bg-ginox-green/20"
                      }`}
                    >
                      {isDocumentSubmitting
                        ? editingDocument
                          ? "Updating..."
                          : "Uploading..."
                        : editingDocument
                          ? "Update Document"
                          : "Upload Document"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setShowDocumentForm(false);
                        setEditingDocument(null);
                        setDocTitle("");
                        setDocFile(null);
                      }}
                      className="flex-1 rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}
            {!showDocumentForm &&
              renderKnowledgeBaseTable(filteredKnowledgeBaseEntries)}
          </div>
        )}

        {activeAdminTab === "translations" && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Translations</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Which languages each article has been written in. Click a row to open it
                and add the missing one.
              </p>
            </div>

            {isTranslationsLoading ? (
              <div className="py-10 flex justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
              </div>
            ) : (
              <>
                {translationSummary && (
                  <div className="rounded-lg border border-border/70 bg-card p-5 space-y-3">
                    <p className="text-sm text-foreground">
                      <span className="tabular-nums">
                        {translationSummary.total}
                      </span>{" "}
                      articles ·{" "}
                      <span className="tabular-nums">
                        {translationSummary.complete}
                      </span>{" "}
                      complete ·{" "}
                      <span className="tabular-nums">
                        {translationSummary.percentComplete ?? 0}%
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-x-8 gap-y-2">
                      {(translationSummary.byLanguage || []).map((lang) => (
                        <p
                          key={lang.language}
                          className="text-xs text-muted-foreground"
                        >
                          <span className="font-mono uppercase text-foreground">
                            {lang.language}
                          </span>{" "}
                          — present{" "}
                          <span className="tabular-nums text-ginox-green">
                            {lang.present}
                          </span>
                          , missing{" "}
                          <span className="tabular-nums text-red-400">
                            {lang.missing}
                          </span>
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
                    <thead>
                      <tr>
                        {["Title", "Status", "Has", "Missing", "Updated"].map((h) => (
                          <th
                            key={h}
                            className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {translationRows.map((row) => (
                        <tr
                          key={row.id}
                          onClick={() => openArticleFromTranslations(row)}
                          className="cursor-pointer hover:bg-white/[0.04] transition"
                        >
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground max-w-md">
                            <span className="line-clamp-1">{row.title}</span>
                            <span className="block text-[10px] uppercase tracking-[0.08em] text-muted-foreground/60">
                              {row.productSection}
                            </span>
                          </td>
                          <td className="border-b border-border/40 px-4 py-3">
                            <Pill
                              className={
                                STAGE_STYLES[row.status] || STAGE_STYLES.draft
                              }
                            >
                              {STAGE_LABELS[row.status] || row.status}
                            </Pill>
                          </td>
                          <td className="border-b border-border/40 px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {row.languages.map((l) => (
                                <Pill
                                  key={l}
                                  className="border-ginox-green/30 bg-ginox-green/10 text-ginox-green"
                                >
                                  {l}
                                </Pill>
                              ))}
                            </div>
                          </td>
                          <td className="border-b border-border/40 px-4 py-3">
                            {row.missing.length === 0 ? (
                              <span className="text-xs text-muted-foreground/50">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {row.missing.map((l) => (
                                  <Pill
                                    key={l}
                                    className="border-red-500/30 bg-red-500/10 text-red-400"
                                  >
                                    {l}
                                  </Pill>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="border-b border-border/40 px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                            {formatDate(row.updatedAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        {/* Custom Confirmation Modal */}
        <SupportConfirmationModal
          isOpen={showDeleteModal}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title={`Delete ${
            deleteModalType === "article" ? "Article" : "Document"
          }`}
          message={`Are you sure you want to delete "${itemToDelete?.title}"? This action cannot be undone.`}
          confirmText="Yes, Delete"
          cancelText="No, Cancel"
          loading={
            deletingArticleId === itemToDelete?.id ||
            deletingDocumentId === itemToDelete?.id
          }
          loadingText="Deleting..."
        />

        {/* Version preview */}
        {previewVersion && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-lg border border-border/70 bg-card p-6 shadow-xl">
              <div className="mb-4">
                <h3 className="text-base font-semibold text-foreground">
                  Version {previewVersion.versionNumber}
                </h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {previewVersion.editorName || "Unknown"} ·{" "}
                  {formatDate(previewVersion.createdAt)} ·{" "}
                  {previewVersion.productSection}
                </p>
              </div>

              {(() => {
                const versionLangs = Object.keys(previewVersion.translations || {});
                const losing = currentLanguages().filter(
                  (l) => !versionLangs.includes(l),
                );
                return (
                  <>
                    <div className="space-y-4">
                      {versionLangs.map((lang) => (
                        <div key={lang}>
                          <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                            {lang}
                          </p>
                          <p className="text-sm text-foreground mb-1.5">
                            {previewVersion.translations[lang]?.title}
                          </p>
                          <div
                            className="prose prose-invert max-w-none rounded-md border border-border/70 bg-white/[0.02] p-3 text-xs text-muted-foreground"
                            dangerouslySetInnerHTML={{
                              __html:
                                previewVersion.translations[lang]?.content || "",
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* A restore is exact, so a version with fewer languages removes the
                        ones it does not have. That is not obvious from "restore". */}
                    {losing.length > 0 && (
                      <div className="mt-4 flex items-start gap-2 rounded-md border border-yellow-500/20 bg-yellow-500/10 p-3">
                        <AlertTriangle className="h-4 w-4 shrink-0 text-yellow-400 mt-0.5" />
                        <p className="text-xs leading-relaxed text-yellow-300">
                          This version has no{" "}
                          <strong>{losing.join(", ").toUpperCase()}</strong>{" "}
                          translation. Restoring it will delete the current one — it stays
                          recoverable from the newer version.
                        </p>
                      </div>
                    )}

                    <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground/70">
                      Restoring changes the text only. The article's stage and AI switch
                      stay as they are.
                    </p>

                    <div className="mt-5 flex gap-2">
                      <button
                        type="button"
                        disabled={isRestoring}
                        onClick={() =>
                          handleRestoreVersion(previewVersion.versionNumber)
                        }
                        className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        {isRestoring ? "Restoring..." : "Restore this version"}
                      </button>
                      <button
                        type="button"
                        disabled={isRestoring}
                        onClick={() => setPreviewVersion(null)}
                        className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
                      >
                        Close
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
